import { HttpService } from 'services/HttpService';
import { API_URL, conf, tenantConfig } from 'config';
import AuthService from 'services/AuthService';
import { httpRequestWrapper } from 'util/Request';
import SelectOption from 'store/types/SelectOption';
import { EventsCategories } from 'store/types/events/EventFilter';
import EventsCartItem from 'store/types/events/EventsCartItem';
import EventFile from 'store/types/events/EventFile';
import AuraAttribute, { PreferenceQuestionAnswer, PrivatePreferencesResponseData } from 'store/types/AuraAttribute';
import { MainEvent } from 'store/types/events/Event';
import Interest from 'store/types/events/Interest';

type EventsResponseData = MainEvent[];
type EventSessionCategoriesResponseData = SelectOption[];
type EventTypesResponseData = SelectOption[];
type EventLocationsResponseData = SelectOption[];
type EventsCartResponseData = EventsCartItem[];
type EventsCategoriesData = EventsCategories[];
type EventsDetailsResponseData = MainEvent[];
type PreferenceQuestionsResponseData = AuraAttribute[];
type InterestResponseData = Interest[];

class EventService {
  public static getEventSessionCategories(): Promise<EventSessionCategoriesResponseData> {
    return new Promise<EventSessionCategoriesResponseData>(
      (resolve: (data: EventSessionCategoriesResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventSessionCategoriesResponseData>(
          HttpService.get(`${API_URL}/events/sessions/categories`, {
            headers: {
              TenantId: tenantConfig.id,
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventTypes(): Promise<EventTypesResponseData> {
    return new Promise<EventTypesResponseData>(
      (resolve: (data: EventTypesResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventTypesResponseData>(
          HttpService.get(`${API_URL}/events/types`, {
            headers: {
              TenantId: tenantConfig.id,
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventLocations(): Promise<EventLocationsResponseData> {
    return new Promise<EventLocationsResponseData>(
      (resolve: (data: EventLocationsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventLocationsResponseData>(
          HttpService.get(`${API_URL}/events/locations`, {
            headers: {
              TenantId: tenantConfig.id,
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventAgeRanges(): Promise<SelectOption[]> {
    return new Promise<SelectOption[]>((resolve: (data: SelectOption[]) => void, reject: (error: string) => void) => {
      httpRequestWrapper<SelectOption[]>(
        HttpService.get(`${API_URL}/events/ages`, {
          headers: {
            TenantId: tenantConfig.id,
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static getPreferenceQuestionsByAlternateId(alternateId: string): Promise<PrivatePreferencesResponseData> {
    return new Promise<PrivatePreferencesResponseData>(
      (resolve: (data: PrivatePreferencesResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<PrivatePreferencesResponseData>(
          HttpService.get(`${API_URL}/events/${alternateId}/preferences`, {
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static updatePreferenceQuestionsByAlternateId(
    alternateId: string,
    data: PreferenceQuestionAnswer[]
  ): Promise<PreferenceQuestionsResponseData> {
    return new Promise<PreferenceQuestionsResponseData>(
      (resolve: (value: PreferenceQuestionsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper(
          HttpService.put(`${API_URL}/events/${alternateId}/preferences`, {
            data,
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventFile(eventId: string, { id, name }: EventFile): Promise<null> {
    return new Promise<null>((resolve: (data: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper<null>(
        HttpService.getFileDownload(`${API_URL}/events/${eventId}/files/${id}`, {
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
            [conf.filenameHeader]: name,
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static getEvents(search?: string): Promise<EventsResponseData> {
    return new Promise<EventsResponseData>(
      (resolve: (data: EventsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventsResponseData>(
          HttpService.get(`${API_URL}/events${search}`, {
            headers: {
              TenantId: tenantConfig.id,
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventDetails(eventsId: string[]): Promise<EventsDetailsResponseData> {
    return new Promise<EventsDetailsResponseData>(
      (resolve: (data: EventsDetailsResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventsDetailsResponseData>(
          HttpService.get(`${API_URL}/events/details`, {
            queryParams: { eventsId },
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventsCategories(search = ''): Promise<EventsCategoriesData> {
    return new Promise<EventsCategoriesData>(
      (resolve: (data: EventsCategoriesData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventsCategoriesData>(
          HttpService.get(`${API_URL}/events/count/${search}`, {
            headers: {
              TenantId: tenantConfig.id,
            },
          }),
          resolve,
          reject
        );
      }
    );
  }

  public static getEventsCart(): Promise<EventsCartResponseData> {
    return new Promise<EventsCartResponseData>(
      (resolve: (data: EventsCartResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<EventsCartResponseData>(
          HttpService.get(`${API_URL}/events/cart`, {
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject,
          false
        );
      }
    );
  }

  public static addEventToCart(
    eventId: string,
    sessions: string[] = [],
    registrantId?: string,
    guestId?: string,
    companyId?: string
  ): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.post(`${API_URL}/events/cart`, {
          data: [{ eventId, sessions, registrantId, companyId, guestId }],
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static addEventsToCart(cartLines: EventsCartItem[]): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.post(`${API_URL}/events/cart`, {
          data: cartLines,
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static addEventToWaitlist(eventId: string, numberOfRegistrants: number, reason?: string): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.post(`${API_URL}/events/waitlist`, {
          data: { eventId, reason, numberOfRegistrants },
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static removeEventFromCart(eventId: string, guestId?: string, registrantId?: string): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.delete(`${API_URL}/events/cart`, {
          queryParams: { eventId, guestId, registrantId },
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static emptyCart(): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.delete(`${API_URL}/events/cart`, {
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static updateCartEventSessions(
    eventId: string,
    sessions: string[] = [],
    registrantId?: string,
    guestId?: string,
    companyId?: string
  ): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.put(`${API_URL}/events/cart`, {
          data: [{ eventId, sessions, registrantId, companyId, guestId }],
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static getInterests(): Promise<InterestResponseData> {
    return new Promise<InterestResponseData>(
      (resolve: (data: InterestResponseData) => void, reject: (error: string) => void) => {
        httpRequestWrapper<InterestResponseData>(
          HttpService.get(`${API_URL}/events/interest`, {
            headers: {
              TenantId: tenantConfig.id,
              authorization: AuthService.getBearerToken(),
            },
          }),
          resolve,
          reject,
          false
        );
      }
    );
  }

  public static createInterest(itemId: string): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.post(`${API_URL}/events/interest?itemId=${itemId}`, {
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }

  public static deleteInterest(id: string): Promise<null> {
    return new Promise<null>((resolve: (value: null) => void, reject: (error: string) => void) => {
      httpRequestWrapper(
        HttpService.delete(`${API_URL}/events/interest/${id}`, {
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }),
        resolve,
        reject
      );
    });
  }
}
export default EventService;
